import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import {
  DeleteUserDailyChallenge,
  DeleteUserMonthlyChallenge,
  GetAdminChallenges,
  GetChallengesHistory,
  GetUserChallenges,
  PostUserDailyChallenge,
  PostUserMonthlyChallenge,
  PutUserDailyChallenge,
  PutUserMonthlyChallenge,
  SetInitialValue
} from './challenges.actions';
import { ChallengesService } from '../services/challenges.service';
import {
  AdminChallengeContent,
  Challenge,
  ChallengeHistory,
  DailyChallengeContent,
  MonthlyChallengeContent
} from '../services/models/challenges.model';
import { tap } from 'rxjs/operators';
import { patch } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { LoadingModel } from 'src/app/_core/state/loading.model';
import { ToastService } from '../../_core/services/toast.service';

export type ChallengesStateModel = LoadingModel & {
  global?: Challenge<AdminChallengeContent>;
  assigned?: Challenge<AdminChallengeContent>;
  daily?: Challenge<DailyChallengeContent>;
  monthly?: Challenge<MonthlyChallengeContent>;
  history?: ChallengeHistory;
  initialValue: number;
};

export const initialState: ChallengesStateModel = {
  global: undefined,
  assigned: undefined,
  daily: undefined,
  monthly: undefined,
  history: undefined,
  isLoading: false,
  initialValue: undefined,
};

@State<ChallengesStateModel>({
  name: 'challenges',
  defaults: initialState
})
@Injectable()
export class ChallengesState {
  private readonly challengesService = inject(ChallengesService);
  private readonly toastService = inject(ToastService);

  @Action(GetAdminChallenges)
  getAdminChallenges(context: StateContext<ChallengesStateModel>): Observable<{ global: Challenge<AdminChallengeContent>, assigned: Challenge<AdminChallengeContent> }> {
    context.patchState({ isLoading: true });
    return this.challengesService.getAdminChallenges().pipe(
      tap(({ global, assigned }) => {
        context.setState(patch<ChallengesStateModel>({ global, assigned, isLoading: false }));
      })
    );
  }

  @Action(GetUserChallenges)
  getUserChallenges(context: StateContext<ChallengesStateModel>): Observable<{ daily: Challenge<DailyChallengeContent>, monthly: Challenge<MonthlyChallengeContent> }> {
    context.patchState({ isLoading: true });
    return this.challengesService.getUserChallenges().pipe(
      tap(({ daily, monthly }) => {
        context.setState(patch<ChallengesStateModel>({ daily, monthly, isLoading: false }));
      })
    );
  }

  @Action(PostUserDailyChallenge)
  postUserDailyChallenge(context: StateContext<ChallengesStateModel>, { target }: PostUserDailyChallenge): Observable<Challenge<DailyChallengeContent>> {
    return this.challengesService.postUserDailyChallenge(target).pipe(
      tap((daily) => {
        context.setState(patch<ChallengesStateModel>({ daily }));
        this.toastService.showToast('user.challenges.challengeCreated');
      })
    );
  }

  @Action(PostUserMonthlyChallenge)
  postUserMonthlyChallenge(context: StateContext<ChallengesStateModel>, { target }: PostUserMonthlyChallenge): Observable<Challenge<MonthlyChallengeContent>> {
    return this.challengesService.postUserMonthlyChallenge(target).pipe(
      tap((monthly) => {
        context.setState(patch<ChallengesStateModel>({ monthly }));
        this.toastService.showToast('user.challenges.challengeCreated');
      })
    );
  }

  @Action(PutUserDailyChallenge)
  putUserDailyChallenge(context: StateContext<ChallengesStateModel>, { target }: PutUserDailyChallenge): Observable<Challenge<DailyChallengeContent>> {
    return this.challengesService.putUserDailyChallenge(target).pipe(
      tap((daily) => {
        context.setState(patch<ChallengesStateModel>({ daily }));
        this.toastService.showToast('user.challenges.challengeUpdated');
      })
    );
  }

  @Action(PutUserMonthlyChallenge)
  putUserMonthlyChallenge(context: StateContext<ChallengesStateModel>, { target }: PutUserMonthlyChallenge): Observable<Challenge<MonthlyChallengeContent>> {
    return this.challengesService.putUserMonthlyChallenge(target).pipe(
      tap((monthly) => {
        context.setState(patch<ChallengesStateModel>({ monthly }));
        this.toastService.showToast('user.challenges.challengeUpdated');
      })
    );
  }

  @Action(DeleteUserDailyChallenge)
  deleteUserDailyChallenge(context: StateContext<ChallengesStateModel>): Observable<void> {
    return this.challengesService.deleteUserDailyChallenge().pipe(
      tap(() => {
        context.setState(patch<ChallengesStateModel>({ daily: undefined }));
        this.toastService.showToast('messages.challenge.removed');
      })
    );
  }

  @Action(DeleteUserMonthlyChallenge)
  deleteUserMonthlyChallenge(context: StateContext<ChallengesStateModel>): Observable<void> {
    return this.challengesService.deleteUserMonthlyChallenge().pipe(
      tap(() => {
        context.setState(patch<ChallengesStateModel>({ monthly: undefined }));
        this.toastService.showToast('messages.challenge.removed');
      })
    );
  }

  @Action(GetChallengesHistory)
  getChallengesHistory(context: StateContext<ChallengesStateModel>, { pageSize, page }: GetChallengesHistory): Observable<ChallengeHistory> {
    context.patchState({ isLoading: true });
    return this.challengesService.getHistoryChallenges(pageSize, page).pipe(
      tap((history) => {
        const state = context.getState();
        let newState = history;

        // If history exists and search for more history entries, then append new history items to previous ones
        if (state.history && page > 1) {
          newState = {
            ...history,
            items: [...state.history.items, ...history.items],
          };
        }

        context.setState(patch<ChallengesStateModel>({ history: newState, isLoading: false }));
      })
    );
  }

  @Action(SetInitialValue)
  setInitialValue(context: StateContext<ChallengesStateModel>, { initialValue }: SetInitialValue) {
    context.setState(patch<ChallengesStateModel>({ initialValue }));
  }
}
